import { maskRocqComments } from './learningResources';

/** Syntax checks complement execution; they do not grade a function's output. */
export function completionCodeIssues(code: string, startingCode: string): string[] {
  const clean = maskRocqComments(code);
  const issues: string[] = [];
  if (!clean.trim()) issues.push('The editor is empty.');
  if (/\b(?:Admitted|Abort|admit)\b/.test(clean)) issues.push('Complete all unfinished proofs.');
  let openProofs = 0;
  for (const sentence of clean.split(/\.(?=\s|$)/)) {
    const text = sentence.replace(/^[\s{}\-+*]+/, '').trim();
    if (/^(?:Theorem|Lemma|Example|Corollary|Proposition|Fact|Remark|Goal)\b/.test(text) && !text.includes(':=')) openProofs++;
    else if (/^(?:Definition|Fixpoint|CoFixpoint)\b/.test(text) && !text.includes(':=')) openProofs++;
    else if (/^Proof\b/.test(text) && openProofs === 0) openProofs++;
    else if (/^(?:Qed|Defined|Save|Admitted|Abort)\b/.test(text)) openProofs = Math.max(0, openProofs - 1);
  }
  // This is only an early rejection. Closed-looking code still needs jsCoq's
  // confirmation; direct definitions never acquire a Qed requirement.
  if (openProofs > 0) issues.push('Close all open proofs with Qed. or Defined. before checking.');
  const declarations = (text: string) => [...maskRocqComments(text).matchAll(
    /\b(?:Theorem|Lemma|Example|Definition|Fixpoint|CoFixpoint|Inductive|CoInductive|Record|Variant|Module)\s+([\w']+)/g,
  )].map(match => match[1]);
  const names = declarations(code);
  for (const name of new Set(declarations(startingCode))) {
    if (!names.includes(name)) issues.push(`Missing required declaration: ${name}`);
  }
  return issues;
}

interface Sentence {
  text: string;
  phase: string;
  coq_sid?: number;
  flags?: { is_comment?: boolean };
}

interface ExecutionManager {
  doc: { sentences: Sentence[] };
  error: unknown[];
  navEnabled?: boolean;
  goNext(focus: boolean): boolean;
  when_ready?: { promise: Promise<unknown> };
  coq: {
    observers: WorkerObserver[];
    goals(sid: number): void;
  } | null;
}

interface WorkerObserver {
  coqGoalInfo?: (sid: number, goals: unknown) => void;
  coqCoqExn?: (error: { msg?: string; pp?: unknown }) => void;
  coqJsonExn?: (message: unknown) => void;
  coqReady?: () => void;
}

export class JsCoqExecutionError extends Error {
  constructor(message: string, readonly needsReset = false) { super(message); }
}

const activeExecutions = new WeakSet<ExecutionManager>();

const phase = (sentence: Sentence) => sentence.phase.toLowerCase();

/** Run through EOF, including commands not yet present in jsCoq's document. */
export async function executeDocument(
  manager: ExecutionManager,
  code: string,
  readCode: () => string,
  options: { timeoutMs?: number; pollMs?: number } = {},
): Promise<{ isComplete: boolean; hasError: boolean; goals?: unknown }> {
  if (activeExecutions.has(manager)) throw new Error('A code check is already running.');
  if (!manager.coq) throw new Error('jsCoq is not ready yet.');
  activeExecutions.add(manager);
  const worker = manager.coq;
  const deadline = Date.now() + (options.timeoutMs ?? 90_000);
  const rawStates = new Map<number, unknown>();
  let workerError: JsCoqExecutionError | undefined;
  let waitingGoal: { sid: number; resolve: (raw: unknown) => void; reject: (error: Error) => void } | undefined;
  const observer: WorkerObserver = {
    coqGoalInfo(sid, raw) {
      rawStates.set(sid, raw);
      if (waitingGoal?.sid === sid) waitingGoal.resolve(raw);
    },
    coqCoqExn(error) {
      const message = error.msg || JSON.stringify(error.pp) || 'jsCoq execution failed.';
      workerError = new JsCoqExecutionError(message, /anomaly|assertion failed|fatal error/i.test(message));
      waitingGoal?.reject(workerError);
    },
    coqJsonExn(message) {
      workerError = new JsCoqExecutionError(String(message), true);
      waitingGoal?.reject(workerError);
    },
    coqReady() { rawStates.clear(); },
  };
  worker.observers.push(observer);
  const unchanged = () => {
    if (workerError) throw workerError;
    if (readCode() !== code) throw new Error('The code changed during checking. Run the check again.');
  };
  const failed = () => manager.error.length > 0 || manager.doc.sentences.some(sentence =>
    ['error', 'failed'].includes(phase(sentence)));
  const matchesEditor = () => {
    const processed = manager.doc.sentences.slice(1).map(sentence => sentence.text).join('');
    return code.startsWith(processed) && !maskRocqComments(code.slice(processed.length)).trim();
  };

  try {
    // JsCoq.start returns a manager before its worker necessarily emits Ready.
    // Adding commands before then can race with initialization of STM state IDs.
    if (manager.when_ready) {
      await boundedWait(manager.when_ready.promise, Math.max(1, deadline - Date.now()),
        'jsCoq did not become ready.');
    }
    while (true) {
      unchanged();
      if (failed()) return { isComplete: false, hasError: true };
      if (Date.now() >= deadline) throw new JsCoqExecutionError('Checking timed out. The level has not been completed.', true);
      if (manager.navEnabled !== false && manager.doc.sentences.every(sentence => phase(sentence) === 'processed')) {
        // false means EOF, not merely that all currently registered commands ran.
        if (!manager.goNext(false) && matchesEditor()) break;
      }
      await new Promise(resolve => setTimeout(resolve, options.pollMs ?? 50));
    }

    const sentences = manager.doc.sentences;
    const last = [...sentences].reverse().find(sentence => sentence.coq_sid && !sentence.flags?.is_comment);
    // The first processed sentence is a jsCoq placeholder, not student code.
    if (sentences.length <= 1 || !last?.coq_sid) return { isComplete: false, hasError: false };
    const sid = last.coq_sid;
    let goals: unknown;
    if (rawStates.has(sid)) {
      // The manager normally requests goals itself after processing a command.
      // Reuse that exact response rather than issuing a duplicate query.
      goals = rawStates.get(sid);
    } else {
      goals = await new Promise<unknown>((resolve, reject) => {
        const timer = setTimeout(() => {
          waitingGoal = undefined;
          reject(new JsCoqExecutionError('No final proof state received. The level has not been completed.', true));
        }, Math.min(10_000, Math.max(1, deadline - Date.now())));
        const settle = (action: () => void) => { clearTimeout(timer); waitingGoal = undefined; action(); };
        waitingGoal = {
          sid,
          resolve: raw => settle(() => resolve(raw)),
          reject: error => settle(() => reject(error)),
        };
        try { worker.goals(sid); }
        catch (error) { settle(() => reject(error)); }
      });
    }
    unchanged();
    if (!matchesEditor()) throw new Error('The document changed during checking. Run the check again.');
    const hasError = failed();
    // An empty goal object is still an open proof, including one awaiting Qed.
    return { isComplete: goals === null && !hasError, hasError, goals };
  } finally {
    const index = worker.observers.indexOf(observer);
    if (index >= 0) worker.observers.splice(index, 1);
    activeExecutions.delete(manager);
  }
}

async function boundedWait<T>(promise: Promise<T>, timeoutMs: number, message: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([promise, new Promise<never>((_, reject) => {
      timer = setTimeout(() => reject(new JsCoqExecutionError(message, true)), timeoutMs);
    })]);
  } finally { clearTimeout(timer); }
}

/** Reset STM after an internal worker failure, keeping the editor text intact. */
export async function recoverEditorWorker(
  manager: Pick<ExecutionManager, 'coq'> & { reset(): Promise<unknown> },
  timeoutMs = 30_000,
): Promise<void> {
  if (!manager.coq) throw new Error('jsCoq is not available.');
  const worker = manager.coq;
  let ready!: () => void;
  const readyPromise = new Promise<void>(resolve => { ready = resolve; });
  const observer: WorkerObserver = { coqReady: () => ready() };
  worker.observers.push(observer);
  try {
    // reset() only retracts processing marks; it does not load or replace text.
    // It resolves after sending Init, so separately await the new Ready event.
    await boundedWait(Promise.all([manager.reset(), readyPromise]), timeoutMs,
      'jsCoq recovery timed out. Your code is still in the editor.');
  } finally {
    const index = worker.observers.indexOf(observer);
    if (index >= 0) worker.observers.splice(index, 1);
  }
}
